import React, { useEffect, useRef } from "react";
import {
  AccountTree,
  AdminPanelSettings,
  AutoAwesome,
  BadgeOutlined,
  BusinessCenter,
  Checklist,
  CheckCircleOutline,
  Dashboard,
  Description,
  FolderOpen,
  Groups,
  Hub,
  Key,
  Lan,
  Palette,
  Policy,
  RocketLaunch,
  Security,
  Settings,
  Shield,
  SyncAlt,
  ViewKanban,
} from "@mui/icons-material";
import { authFlowSteps, automationRules, companyOnboardingSteps, lyricliousImportOptions, modules, permissionGroups, relationshipModels, roleTemplates } from "../../../data/workspaceData";
import { documentationId, documentationItems, moduleIcons } from "../../../data/workspaceConfig";
import { SectionHeader } from "../common/WorkspaceChrome";
import { AttendanceView } from "../sections/people/AttendanceView";
import { CrmView } from "../sections/clients-crm/CrmView";
import { DashboardView } from "../sections/dashboard/DashboardView";
import { DocsView } from "../sections/docs/DocsView";
import { FinanceView } from "../sections/finance/FinanceView";
import { MeetingsView } from "../sections/schedule/MeetingsView";
import { MyWorkView } from "../sections/my-work/MyWorkView";
import { PeopleView } from "../sections/people/PeopleView";
import { ProjectsView } from "../sections/projects/ProjectsView";
import { ReportsView } from "../sections/reports/ReportsView";
import { SettingsView } from "../sections/settings/SettingsView";
import { SprintsView } from "../sections/projects/SprintsView";

const documentationTopicIcons = {
  "Product Blueprint": AccountTree,
  Dashboard,
  "My Work": Checklist,
  Work: ViewKanban,
  Projects: BusinessCenter,
  Sprints: RocketLaunch,
  Meetings: Groups,
  People: BadgeOutlined,
  CRM: Hub,
  Attendance: CheckCircleOutline,
  Docs: Description,
  Finance: AccountTree,
  Reports: Dashboard,
  Settings,
  "Company Onboarding": BusinessCenter,
  Relationships: Lan,
  "Roles & Permissions": Shield,
  "Work Taxonomy": AccountTree,
  "SSO & Import": Key,
  Branding: Palette,
  Automations: AutoAwesome,
};

const workTopicIcons = {
  Overview: Dashboard,
  Board: ViewKanban,
  Sprints: RocketLaunch,
  Tasks: Checklist,
  Tickets: Description,
  Bugs: Security,
  Epics: AccountTree,
  Backlog: FolderOpen,
  Approvals: CheckCircleOutline,
  Automation: AutoAwesome,
  Templates: Description,
  Reports: Dashboard,
};

function ProductBlueprintView() {
  return (
    <>
      <section className="module-header-card">
        <SectionHeader
          eyebrow="Documentation"
          title="Workspace product blueprint"
          text="Implementation guidance, module planning, and product direction for the Workspace PWA."
        />
      </section>
      <DashboardView />
    </>
  );
}

function CompanyOnboardingDocs() {
  return (
    <div className="content-grid">
        <section className="panel">
          <SectionHeader
            eyebrow="Company Setup"
            title="Flexible onboarding for every company type"
            text="Start simple, then add structure only when the company needs it."
          />
          <div className="step-list">
            {companyOnboardingSteps.map((step, index) => (
              <div className="step-item" key={step}>
                <span className="documentation-step-icon">{index === 0 ? <BusinessCenter fontSize="small" /> : index === 1 ? <Groups fontSize="small" /> : index === 2 ? <Settings fontSize="small" /> : <RocketLaunch fontSize="small" />}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="panel">
          <SectionHeader
            eyebrow="Lyriclious SSO"
            title="One account across Lyriclious and Workspace"
            text="If a user is already signed in to Lyriclious, Workspace should continue without asking them to sign in again."
          />
          <div className="auth-flow">
            {authFlowSteps.map((step, index) => (
              <div className="auth-step" key={step}>
                <span className="documentation-step-icon secure">{index === 0 ? <Key fontSize="small" /> : index === 1 ? <Security fontSize="small" /> : index === 2 ? <SyncAlt fontSize="small" /> : <CheckCircleOutline fontSize="small" />}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </section>
    </div>
  );
}

function RelationshipDocs() {
  return (
    <section className="panel">
        <SectionHeader
          eyebrow="Relationship Options"
          title="No forced hierarchy"
          text="Companies can use formal reporting, loose collaboration, client-owned work, or custom relationships."
        />
        <div className="cards-grid">
          {relationshipModels.map((model) => (
            <article className="info-card" key={model.title}>
              <Lan fontSize="small" />
              <h3>{model.title}</h3>
              <p>{model.description}</p>
              <div className="chip-row">
                {model.examples.map((example) => (
                  <span key={example}>{example}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
  );
}

function SsoImportDocs() {
  return (
    <section className="panel">
        <SectionHeader
          eyebrow="Data Import"
          title="Bring useful Lyriclious data into Workspace"
          text="Imported data should be optional and permission-aware, so Workspace setup feels fast without over-sharing."
        />
        <div className="import-list doc-import-grid">
          {lyricliousImportOptions.map((option) => (
            <div key={option}>
              <SyncAlt fontSize="small" />
              <span>{option}</span>
            </div>
          ))}
        </div>
      </section>
  );
}

function ModulesDocs() {
  return (
    <section className="panel">
        <SectionHeader
          eyebrow="Modules"
          title="Workspace OS map"
          text="The app is structured to grow from MVP into scheduling, docs, wallet, AI, reports, and client portal modules."
        />
        <div className="module-grid">
          {modules.map((module) => {
            const Icon = moduleIcons[module] || Dashboard;
            return (
              <article key={module}>
                <Icon fontSize="small" />
                <span>{module}</span>
              </article>
            );
          })}
        </div>
      </section>
  );
}

function AutomationDocs() {
  return (
    <section className="panel">
        <SectionHeader
          eyebrow="Automations"
          title="Operational rules that reduce manual follow-up"
          text="Automations should remain understandable, editable, and scoped per company."
        />
        <div className="automation-list">
          {automationRules.map((rule) => (
            <article key={rule}>
              <span className="documentation-rule-icon"><AutoAwesome fontSize="small" /></span>
              <p>{rule}</p>
            </article>
          ))}
        </div>
      </section>
  );
}

function RolesPermissionsDocs() {
  return (
    <section className="panel">
      <SectionHeader
        eyebrow="Roles & Permissions"
        title="Role templates plus custom access"
        text="Use default roles for speed, then customize access per company, department, project, or client portal."
      />
      <div className="role-grid">
        {roleTemplates.map((role) => (
          <span key={role}><BadgeOutlined fontSize="inherit" />{role}</span>
        ))}
      </div>
      <div className="permission-list settings-permissions">
        {permissionGroups.map((group) => (
          <article key={group.title}>
            <strong><AdminPanelSettings fontSize="small" />{group.title}</strong>
            <p>{group.permissions.join(" · ")}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function BrandingDocs() {
  const brandingItems = [
    ["Company logo", "Collected from the workspace client and saved in Administration > Branding. It appears in the sidebar and workspace shell.", BusinessCenter],
    ["Browser icon", "Use a favicon/ICO, PNG, or SVG URL for the browser title bar. If blank, Workspace uses the Lyriclious fallback.", Description],
    ["Theme color", "Super admins can use the default Lyriclious color or an organization color saved with the workspace record.", Palette],
    ["Fallback", "Blank logo or favicon fields intentionally keep the built-in Lyriclious Workspace icon.", Shield],
  ];

  return (
    <section className="panel">
      <SectionHeader
        eyebrow="Branding"
        title="Client logo, favicon, and workspace theme"
        text="Branding belongs in Administration because it affects the whole workspace, not only one user's personal appearance settings."
      />
      <div className="settings-grid">
        {brandingItems.map(([title, text, Icon]) => (
          <article key={title}>
            <strong><Icon fontSize="small" />{title}</strong>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const workTopicDocs = [
  ["Overview", "Management snapshot for blocked work, sprint load, open tickets, bugs, approvals, and delivery risk."],
  ["Board", "Kanban movement across planned, active, review, blocked, and done with owners and status history."],
  ["Sprints", "Sprint goals, scope, story points, assignees, blockers, dates, and completion health for each delivery cycle."],
  ["Tasks", "Actionable work with owner, assignees, priority, due date, labels, project/client link, and activity history."],
  ["Tickets", "Client or internal requests with workflow states, permissioned status changes, comments, attachments, and audit history."],
  ["Bugs", "Defects and regressions with severity, affected area, reproduction notes, QA ownership, and release readiness."],
  ["Epics", "Large initiatives grouped across tasks, bugs, tickets, backlog items, and sprint execution."],
  ["Backlog", "Unplanned ideas, future scope, client requests, and deferred work waiting for prioritization."],
  ["Approvals", "Manager, client, QA, finance, or admin gates before work moves forward or closes."],
  ["Automation", "Rules that assign, notify, escalate, move status, create follow-up work, or trigger approvals."],
  ["Templates", "Reusable formats for bug triage, client request intake, sprint tasks, approvals, and recurring work."],
  ["Reports", "Throughput, blockers, workload, overdue items, sprint completion, bug health, and export-ready summaries."],
];

const workDataRules = [
  "Every work item should save permanent history and a readable activity feed.",
  "Status changes should check role, team, project, client, and workflow permissions before saving.",
  "View sessions and presence should be temporary or aggregated, not stored forever on each refresh.",
  "Tasks, tickets, bugs, sprints, epics, templates, and approvals should share one Work record model with type-specific fields.",
];

function WorkDocs() {
  return (
    <>
      <section className="panel">
        <SectionHeader
          eyebrow="Work"
          title="Tasks, tickets, bugs, sprints, epics, approvals, and automation"
          text="Work is the execution center. It merges task management and ticketing while still keeping each work type clear enough for enterprise flows."
        />
        <div className="settings-grid work-doc-topic-grid">
          {workTopicDocs.map(([title, text]) => (
            <article className="documentation-work-topic" key={title}>
              <strong>{React.createElement(workTopicIcons[title] || Description, { fontSize: "small" })}{title}</strong>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="panel">
        <SectionHeader
          eyebrow="Implementation Rules"
          title="How Work should behave behind the UI"
          text="The UI should never be static-only. Work records need permissions, audit history, activity, presence, reports, and reusable workflows."
        />
        <div className="permission-list settings-permissions">
          {workDataRules.map((rule) => (
            <article key={rule}>
              <strong><Policy fontSize="small" />{rule.split(" should ")[0]}</strong>
              <p>{rule}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function WorkTaxonomyDocs({ workView, setWorkView }) {
  return (
    <>
      <WorkDocs />
      <MyWorkView workView={workView} setWorkView={setWorkView} />
      <ModulesDocs />
    </>
  );
}

function DocumentationSection({ label, children }) {
  const Icon = documentationTopicIcons[label] || Description;
  return (
    <section className="documentation-section" id={documentationId(label)}>
      <div className="documentation-anchor-title">
        <span>Documentation</span>
        <div className="documentation-title-row"><i><Icon fontSize="small" /></i><h2>{label}</h2></div>
      </div>
      {children}
    </section>
  );
}

export function DocumentationView({
  activeLabel = "Product Blueprint",
  hiddenTopics = [],
  onSelectTopic,
  onActiveTopicChange,
  workView,
  setWorkView,
  settingsProps,
  showIndex = true,
}) {
  const activeTopicChangeRef = useRef(onActiveTopicChange);
  const visibleDocumentationItems = documentationItems.filter(
    (item) => !hiddenTopics.includes(item)
  );

  useEffect(() => {
    activeTopicChangeRef.current = onActiveTopicChange;
  }, [onActiveTopicChange]);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll(".documentation-section[id]"));
    if (!sections.length) return undefined;

    let frame = 0;
    const updateActiveTopic = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const readingLine = window.innerHeight * 0.3;
        let activeSection = sections[0];
        sections.forEach((section) => {
          if (section.getBoundingClientRect().top <= readingLine) activeSection = section;
        });
        const label = documentationItems.find((item) => documentationId(item) === activeSection.id);
        if (label) activeTopicChangeRef.current?.(label);
      });
    };

    updateActiveTopic();
    window.addEventListener("scroll", updateActiveTopic, { passive: true });
    window.addEventListener("resize", updateActiveTopic);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateActiveTopic);
      window.removeEventListener("resize", updateActiveTopic);
    };
  }, []);

  return (
    <div className="documentation-page">
      {showIndex ? <section className="documentation-index" aria-label="Documentation topics">
        {visibleDocumentationItems.map((item) => (
          (() => {
            const Icon = documentationTopicIcons[item] || Description;
            return (
          <button
            className={activeLabel === item ? "active" : ""}
            key={item}
            onClick={() => onSelectTopic?.(item)}
            type="button"
          >
            <Icon fontSize="small" />
            <span>{item}</span>
          </button>
            );
          })()
        ))}
      </section> : null}
      {!hiddenTopics.includes("Product Blueprint") ? (
        <DocumentationSection label="Product Blueprint">
          <ProductBlueprintView />
        </DocumentationSection>
      ) : null}
      <DocumentationSection label="Dashboard">
        <DashboardView activeTab="Overview" setActiveTab={() => {}} />
      </DocumentationSection>
      <DocumentationSection label="My Work">
        <MyWorkView workView={workView} setWorkView={setWorkView} />
      </DocumentationSection>
      <DocumentationSection label="Work">
        <WorkDocs />
      </DocumentationSection>
      <DocumentationSection label="Projects">
        <ProjectsView />
      </DocumentationSection>
      <DocumentationSection label="Sprints">
        <SprintsView />
      </DocumentationSection>
      <DocumentationSection label="Meetings">
        <MeetingsView />
      </DocumentationSection>
      <DocumentationSection label="People">
        <PeopleView documentationMode />
      </DocumentationSection>
      <DocumentationSection label="CRM">
        <CrmView />
      </DocumentationSection>
      <DocumentationSection label="Attendance">
        <AttendanceView />
      </DocumentationSection>
      <DocumentationSection label="Docs">
        <DocsView />
      </DocumentationSection>
      <DocumentationSection label="Finance">
        <FinanceView />
      </DocumentationSection>
      <DocumentationSection label="Reports">
        <ReportsView />
      </DocumentationSection>
      <DocumentationSection label="Settings">
        <SettingsView documentationMode />
      </DocumentationSection>
      <DocumentationSection label="Company Onboarding">
        <CompanyOnboardingDocs />
      </DocumentationSection>
      <DocumentationSection label="Relationships">
        <RelationshipDocs />
      </DocumentationSection>
      <DocumentationSection label="Roles & Permissions">
        <RolesPermissionsDocs />
      </DocumentationSection>
      <DocumentationSection label="Work Taxonomy">
        <WorkTaxonomyDocs workView={workView} setWorkView={setWorkView} />
      </DocumentationSection>
      <DocumentationSection label="SSO & Import">
        <SsoImportDocs />
      </DocumentationSection>
      <DocumentationSection label="Branding">
        <BrandingDocs />
      </DocumentationSection>
      <DocumentationSection label="Automations">
        <AutomationDocs />
      </DocumentationSection>
    </div>
  );
}
